import { LatLngBounds, latLngBounds, LatLngExpression } from 'leaflet';
import { useMap } from 'react-leaflet';
import {
  getCombinedLevelAssetUserBounds,
  getCoordsFromAssets,
  getCoordsFromUsers,
} from '../utils/utils';
import L from 'leaflet';

import { UserType } from 'types/userInfo';
import { useContext } from 'react';
import { VenueFilesContext } from '../contexts/venuefiles.context';
import { AssetsContext } from '../contexts/assets.context';
import { UsersContext } from '../contexts/users.context';
import { EstimateType } from 'types/Estimate';

export default function useMapHooks() {
  const map = useMap(); // note: requires components to be decendant of mapcontainer to invoke this
  const { levelsFeatures } = useContext(VenueFilesContext);
  const { rawIndoorAssets, rawOutsideMappedBuildingsAssets } = useContext(AssetsContext);
  const { usersMarkers, usersOutsideMappedBuildingsMarkers } = useContext(UsersContext);

  // for obtaining latlong at cursor.
  // useMapEvents({
  //   click: (ev) => console.log(ev.latlng),
  // });

  function recenterMapToCombinedMarkerBounds(assets: EstimateType[], users: UserType[]) {
    if (assets.length < 1 && users.length < 1) return;

    const assetCoords: any = getCoordsFromAssets(assets);
    const userCoords: any = getCoordsFromUsers(users);
    const combinedCoords = [...assetCoords, ...userCoords];
    const bounds = latLngBounds(assetCoords[0]);

    bounds.extend(combinedCoords);

    map.panInsideBounds(bounds, { animate: false });
  }

  function recenterMapToAssetMarkerBounds(assets: EstimateType[]) {
    if (assets.length < 1) return;

    const assetCoords: any = getCoordsFromAssets(assets);
    const bounds = latLngBounds(assetCoords[0]);

    bounds.extend(assetCoords);

    map.panInsideBounds(bounds, {
      noMoveStart: true,
    });
  }

  function removeMapLayerByClassName(className: string) {
    // programatically remove map tooltips,
    map.eachLayer((layer: any) => {
      if (layer.options && layer.options.className === className) {
        map.removeLayer(layer);
      }
    });
  }

  function _fitBounds(bounds: LatLngBounds, maxZoom: number = 21) {
    const northWest = bounds.getNorthWest();
    const southEast = bounds.getSouthEast();

    const latDiff = Math.abs(northWest.lat - southEast.lat);
    const lngDiff = Math.abs(southEast.lng - northWest.lng);

    // If shape is very small or narrow apply more padding 
    // to make the minimum dimension of the bounding box 
    // be at least as large as narrowThreshold
    const narrowThreshold = 0.0005;
    const narrowestDimensionDiff = Math.min(latDiff, lngDiff);
    const isNarrow = narrowestDimensionDiff < narrowThreshold;
    const paddingRatio = (narrowThreshold - narrowestDimensionDiff) / narrowestDimensionDiff;

    map.fitBounds(isNarrow ? bounds.pad(paddingRatio) : bounds, {
       padding: [50, 50],
       maxZoom: maxZoom 
    });
  }

  function fitMapToBoundsOfMultipleGeoJSONs(geoJSONData: any[], maxZoom: number = 21) {
    const allBounds = geoJSONData.map(g => L.geoJSON(g).getBounds());
    const bounds = allBounds[0];
    for (const currentBounds of allBounds){
      bounds.extend(currentBounds);
    }
    _fitBounds(bounds, maxZoom);
  }

  function panToLatLong(eTargetLatLong: LatLngExpression) {
    map.panTo(eTargetLatLong);
  }

  function zoomMapToSingleAsset(asset: EstimateType) {
    const { latitude, longitude } = asset.location.coordinates;
    map.flyTo([latitude, longitude], 20);
  }

  function checkAssetLatLngIsWithinMapBounds(assetLatLongArr: any) {
    return map.getBounds().contains(assetLatLongArr);
  }

  function panMapToCombinedLevelsAssetUserBounds() {
    const combinedAssetBounds = [...rawIndoorAssets, ...rawOutsideMappedBuildingsAssets];
    const combinedUserBounds = [...usersMarkers, ...usersOutsideMappedBuildingsMarkers];
    const bounds = getCombinedLevelAssetUserBounds(
      combinedAssetBounds,
      combinedUserBounds,
      levelsFeatures,
    );

    map.fitBounds(bounds, { animate: true });
  }

  return {
    removeMapLayerByClassName,
    fitMapToBoundsOfMultipleGeoJSONs,
    panToLatLong,
    zoomMapToSingleAsset,
    recenterMapToAssetMarkerBounds,
    recenterMapToCombinedMarkerBounds,
    checkAssetLatLngIsWithinMapBounds,
    panMapToCombinedLevelsAssetUserBounds
  };
}
